import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Award, ClipboardList, RotateCcw, Loader2,
  ChevronLeft, ChevronRight, Sparkles,
} from "lucide-react";

/**
 * ModuleQuiz — a one-question-at-a-time quiz flow.
 * Server contract:
 *   GET  /api/modules/{slug}/quiz         → { title, pass_pct, questions[] }  (no answers)
 *   POST /api/modules/{slug}/quiz/submit  → { score_pct, correct, total, passed, pass_pct, results[] }
 *
 * UX rules baked in:
 *  - Show a single question at a time (full-bleed card).
 *  - After the user picks an answer, briefly confirm the choice, then auto-advance.
 *  - On the last question, submitting scores the whole quiz.
 *  - Need pass_pct (80%) to unlock module completion. Failing = must retake.
 */
export default function ModuleQuiz({ slug, moduleName, initialProgress, onPassed }) {
  const [quiz, setQuiz] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [started, setStarted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({}); // { [qIndex]: "A" | "True" }
  const [locking, setLocking] = useState(false); // brief pause after pick before auto-advance
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);   // { score_pct, correct, total, passed, pass_pct, results }
  const [showReview, setShowReview] = useState(false);
  // Persisted review-snapshot flow for the "already passed" state. Hooks
  // are declared here (top of component) so they run in the same order
  // on every render, regardless of which branch below is taken.
  const [reviewSnap, setReviewSnap] = useState(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [reviewOpenPersisted, setReviewOpenPersisted] = useState(false);
  // Cool-down seconds remaining for a retake (0 = ready to submit)
  const [cooldown, setCooldown] = useState(0);

  // Tick the cool-down timer once per second while active.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((n) => (n > 0 ? n - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  useEffect(() => {
    setQuiz(null); setLoadErr(null); setAnswers({}); setResult(null);
    setStarted(false); setCurrent(0); setShowReview(false); setLocking(false);
    setCooldown(0);
    api.get(`/modules/${slug}/quiz`)
      .then((r) => {
        setQuiz(r.data);
        const remaining = r.data?.retake?.cooldown_remaining_seconds || 0;
        if (remaining > 0) setCooldown(remaining);
      })
      .catch((e) => setLoadErr(formatError(e)));
  }, [slug]);

  const total = quiz?.questions?.length || 0;
  const q = quiz?.questions?.[current];
  const chosen = answers[current];
  const isLast = current === total - 1;

  const pickAnswer = (letter) => {
    if (locking) return;
    setAnswers((prev) => ({ ...prev, [current]: letter }));
    setLocking(true);
    // Show the confirmation moment, then advance (or submit if we're on the last card)
    setTimeout(() => {
      setLocking(false);
      if (isLast) {
        submitFull({ ...answers, [current]: letter });
      } else {
        setCurrent((c) => Math.min(c + 1, total - 1));
      }
    }, 500);
  };

  const submitFull = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const payload = {
        answers: quiz.questions.map((_, i) => finalAnswers[i] || ""),
        attempt_token: quiz.attempt_token,
      };
      const { data } = await api.post(`/modules/${slug}/quiz/submit`, payload);
      setResult(data);
      // Prime the cool-down timer from the response itself so the fail
      // banner shows "Wait X hours Y minutes before retaking" immediately
      // — no page refresh needed. Passing zeroes the cool-down server-side.
      const remaining = data?.retake?.cooldown_remaining_seconds || 0;
      setCooldown(remaining);
      if (data.passed) {
        toast.success(`Passed! ${data.correct}/${data.total} correct`);
        // Publish so any mounted list route (Modules grid, Course
        // overview, sidebar progress ring) can refetch and drop the
        // lock icon on the freshly-unlocked next module.
        try {
          window.dispatchEvent(new CustomEvent("cpg:module-progress", {
            detail: { slug, passed: true, score_pct: data.score_pct },
          }));
        } catch (_) { /* stale runtime — ignore */ }
        onPassed?.(data);
      }
      // Fail path: no toast on purpose. The fail result banner + cool-down
      // button already carry the "you didn't pass, wait X before retaking"
      // signal. A second red toast pointing at a "Review the misses below"
      // section would be misleading — Review is gated to passing users.
    } catch (err) {
      // 429 = cool-down still active for a retake. The backend returns
      // structured detail with `cooldown_remaining_seconds` so we can
      // show a countdown instead of a generic error.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail?.cooldown_remaining_seconds) {
        setCooldown(detail.cooldown_remaining_seconds);
        toast.error(detail.message || "Cool-down active — please wait before retaking.");
      } else {
        toast.error(formatError(err));
      }
    } finally { setSubmitting(false); }
  };

  const retake = async () => {
    // Fetch a fresh shuffled question set (new attempt_token) and reset UI.
    setAnswers({}); setResult(null); setShowReview(false);
    setCurrent(0); setStarted(true);
    try {
      const r = await api.get(`/modules/${slug}/quiz`);
      setQuiz(r.data);
      const remaining = r.data?.retake?.cooldown_remaining_seconds || 0;
      if (remaining > 0) setCooldown(remaining);
    } catch { /* keep the old quiz object; submit will 429 if truly locked */ }
  };

  // ---------------------------------------------------------------- Renderers

  if (loadErr) {
    return (
      <div className="surface p-8 text-center max-w-2xl" data-testid="quiz-unavailable">
        <ClipboardList className="w-6 h-6 text-muted-foreground mx-auto" />
        <p className="mt-3 font-display text-xl">Quiz not available yet</p>
        <p className="text-sm text-muted-foreground mt-2">{loadErr}</p>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="surface p-8 flex items-center gap-3 text-sm text-muted-foreground" data-testid="quiz-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading quiz…
      </div>
    );
  }

  // Result screen ---------------------------------------------------
  if (result) {
    return (
      <div className="max-w-3xl" data-testid={`quiz-${slug}`}>
        <ResultCard result={result} moduleName={moduleName}
          onReview={() => setShowReview((v) => !v)}
          onRetake={retake}
          reviewOpen={showReview}
          cooldown={cooldown}
        />
        {showReview && (
          <div className="mt-6 grid gap-4" data-testid="quiz-review-list">
            {quiz.questions.map((q, i) => (
              <QuestionReviewCard key={i} q={q} index={i} review={result.results?.[i]} />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Intro / start screen -------------------------------------------
  // Completed short-circuit: if the user has already passed this quiz
  // (persisted `quiz_passed_at` on user_progress), we render a completed
  // card in place of the Start Quiz intro. This survives refresh + re-login
  // + tab switches because it's driven by the parent's `initialProgress`,
  // which is refetched from `/modules/{slug}` on every mount.
  const loadReview = async () => {
    if (reviewSnap) { setReviewOpenPersisted((v) => !v); return; }
    setReviewLoading(true); setReviewError("");
    try {
      const r = await api.get(`/modules/${slug}/quiz/review`);
      setReviewSnap(r.data);
      setReviewOpenPersisted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail
        || "Review isn't available yet — pass the quiz once to unlock it.";
      setReviewError(msg);
      toast.info(msg);
    } finally { setReviewLoading(false); }
  };

  if (!started && initialProgress?.quiz_passed_at) {
    const best = initialProgress.best_score || 0;
    const passedAt = initialProgress.quiz_passed_at;
    let passedLabel = "";
    try {
      passedLabel = new Date(passedAt).toLocaleDateString(undefined,
        { year: "numeric", month: "short", day: "numeric" });
    } catch { /* leave blank */ }
    return (
      <div className="max-w-2xl" data-testid={`quiz-completed-${slug}`}>
        <div className="rounded-2xl p-8 md:p-10 grad-band text-white on-dark" data-surface="dark">
          <div className="w-14 h-14 rounded-2xl grid place-items-center bg-white/20">
            <Award className="w-7 h-7" />
          </div>
          <p className="mt-4 text-[10px] uppercase tracking-widest font-semibold text-white/80">
            Certification quiz completed
          </p>
          <h2 className="font-display text-4xl mt-1">
            {moduleName || "This module"} · <span className="text-white/85">Passed</span>
          </h2>
          <p className="mt-3 text-sm md:text-base max-w-lg text-white/90">
            You passed this module's certification quiz{best ? ` with a best score of ${best}%` : ""}
            {passedLabel ? ` on ${passedLabel}` : ""}. This module is now complete on your certificate journey.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-white text-shell-side"
                  data-testid="quiz-completed-badge">
              <CheckCircle2 className="w-3.5 h-3.5" /> Completed
            </span>
            {best ? (
              <span className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-widest bg-shell-side text-white"
                    data-testid="quiz-completed-best-score">
                Best score {best}%
              </span>
            ) : null}
            {/* Review Answers is offered only to passed users — the
                persisted review snapshot lives on the server and is
                gated by the same "must have passed" backend check. */}
            <button
              onClick={loadReview}
              disabled={reviewLoading}
              className="text-xs font-semibold rounded-full px-4 py-2 bg-white text-shell-side hover:bg-white/90 disabled:opacity-60"
              data-testid={`quiz-completed-review-${slug}`}
            >
              {reviewLoading
                ? "Loading…"
                : reviewOpenPersisted ? "Hide answers" : "Review answers"}
            </button>
          </div>
          {reviewError && !reviewOpenPersisted ? (
            <p className="mt-3 text-xs text-white/80">{reviewError}</p>
          ) : null}
        </div>
        {reviewOpenPersisted && reviewSnap ? (
          <div className="mt-6 space-y-4" data-testid={`quiz-completed-review-list-${slug}`}>
            {reviewSnap.questions?.map((q, i) => (
              <QuestionReviewCard key={i} q={q} index={i} review={reviewSnap.results?.[i]} />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  if (!started) {
    const best = initialProgress?.best_score || 0;
    // If the retake cool-down is active — because the user just failed
    // an attempt — surface the countdown here so they don't burn an
    // entire quiz session only to hit the 429 on submit. The `useEffect`
    // above already primes `cooldown` from `retake.cooldown_remaining_seconds`
    // returned by GET /modules/{slug}/quiz.
    const cooldownActive = cooldown > 0;
    return (
      <div className="surface p-8 max-w-2xl" data-testid={`quiz-intro-${slug}`}>
        <p className="chip-amber inline-flex items-center gap-1">
          <ClipboardList className="w-3 h-3" /> Certification Quiz
        </p>
        <h2 className="font-display text-3xl mt-3">{quiz.title}</h2>
        <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
          <IntroStat label="Questions" value={total} />
          <IntroStat label="Passing score" value={`${quiz.pass_pct}%`} />
          <IntroStat label="Your best" value={best ? `${best}%` : "—"} />
        </div>
        <ul className="mt-5 text-sm text-muted-foreground list-disc pl-5 space-y-1">
          <li>You'll see <strong>one question at a time</strong> — pick an answer to advance.</li>
          <li>Reach {quiz.pass_pct}% to earn the badge and unlock this module's completion.</li>
          <li>Miss the mark? You can retake after a short cool-down (fresh, randomised question set each time).</li>
        </ul>
        {cooldownActive ? (
          <div
            className="mt-6 rounded-2xl border border-shell-border bg-muted/60 p-4"
            data-testid="quiz-cooldown-banner"
          >
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Retake cool-down active</p>
            <p className="text-sm mt-1">
              You can retake this quiz in{" "}
              <strong data-testid="quiz-cooldown-remaining">{formatCooldown(cooldown)}</strong>.
              We shuffle to a fresh question set the moment the timer hits zero.
            </p>
            <button
              type="button"
              disabled
              data-testid="quiz-start"
              className="btn-primary mt-4 opacity-50 cursor-not-allowed"
              aria-disabled="true"
              title={`Wait ${formatCooldown(cooldown)} before retaking`}
            >
              <Sparkles className="w-4 h-4" /> Wait {formatCooldown(cooldown)}
            </button>
          </div>
        ) : (
          <button onClick={() => { setStarted(true); setCurrent(0); }}
            className="btn-primary mt-6" data-testid="quiz-start">
            <Sparkles className="w-4 h-4" /> Start quiz
          </button>
        )}
      </div>
    );
  }

  // In-quiz — single question at a time ----------------------------
  const isTF = q.type === "TF";
  const letters = isTF ? ["True", "False"] : ["A", "B", "C", "D"].slice(0, q.options.length);
  const pct = total ? ((current + (chosen ? 1 : 0)) / total) * 100 : 0;

  return (
    <div className="max-w-2xl mx-auto" data-testid={`quiz-${slug}`}>
      {/* Progress meter */}
      <div className="mb-4 flex items-center justify-between text-xs uppercase tracking-widest font-semibold text-muted-foreground">
        <span>Question <span className="text-foreground">{current + 1}</span> of {total}</span>
        <span>{Math.round(pct)}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-6" data-testid="quiz-progress-bar">
        <div className="h-full bg-gradient-to-r from-primary to-shell-accent transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>

      {/* Question card with keyed transition */}
      <div key={current}
        className="surface p-7 md:p-9 animate-in fade-in slide-in-from-right-3 duration-300"
        data-testid={`quiz-q-${current}`}>
        <p className="text-[10px] uppercase tracking-widest text-primary font-bold">
          {isTF ? "True / False" : q.type === "SC" ? "Scenario" : "Multiple choice"}
        </p>
        <h3 className="font-display text-2xl leading-snug mt-2">{q.q}</h3>

        <div className="mt-6 grid gap-3">
          {q.options.map((opt, i) => {
            const letter = letters[i];
            const isChosen = chosen === letter;
            return (
              <button
                key={i}
                type="button"
                disabled={locking && !isChosen}
                onClick={() => pickAnswer(letter)}
                data-testid={`quiz-q-${current}-opt-${letter}`}
                className={[
                  "w-full text-left rounded-2xl border px-4 py-3.5 text-sm flex items-start gap-3 transition-all duration-200",
                  isChosen
                    ? "border-primary bg-primary/10 shadow-sm scale-[1.01]"
                    : locking
                    ? "border-shell-border bg-white/60 opacity-50"
                    : "border-shell-border bg-white hover:border-primary hover:-translate-y-0.5",
                ].join(" ")}
              >
                <span className={[
                  "w-8 h-8 rounded-full grid place-items-center text-xs font-bold shrink-0 transition-colors",
                  isChosen ? "bg-primary text-white" : "bg-muted text-foreground",
                ].join(" ")}>
                  {isTF ? letter[0] : letter}
                </span>
                <span className="flex-1 pt-1">{opt}</span>
                {isChosen && <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-1" />}
              </button>
            );
          })}
        </div>

        {/* Compact nav — mainly to let users go back to review earlier picks */}
        <div className="mt-6 flex items-center justify-between text-xs">
          <button
            onClick={() => setCurrent((c) => Math.max(0, c - 1))}
            disabled={current === 0 || locking || submitting}
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
            data-testid="quiz-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>
          {submitting ? (
            <span className="inline-flex items-center gap-1 text-primary font-semibold" data-testid="quiz-submitting">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Scoring…
            </span>
          ) : locking ? (
            <span className="inline-flex items-center gap-1 text-primary font-semibold">
              {isLast ? "Submitting…" : "Locking in…"} <ChevronRight className="w-3.5 h-3.5" />
            </span>
          ) : (
            <span className="text-muted-foreground">
              {isLast ? "Pick an answer to finish" : "Pick an answer to continue"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Result UI

function ResultCard({ result, moduleName, onReview, onRetake, reviewOpen, cooldown = 0 }) {
  const pass = result.passed;
  const cooldownLabel = formatCooldown(cooldown);
  return (
    <div
      className={[
        "rounded-2xl p-8 md:p-10",
        pass ? "grad-band text-white" : "border-2 border-pink-300 bg-pink-50 text-pink-950",
      ].join(" ")}
      data-testid={pass ? "quiz-pass-banner" : "quiz-fail-banner"}
    >
      <div className={[
        "w-14 h-14 rounded-2xl grid place-items-center",
        pass ? "bg-white/20" : "bg-pink-100 text-pink-700",
      ].join(" ")}>
        {pass ? <Award className="w-7 h-7" /> : <RotateCcw className="w-7 h-7" />}
      </div>
      <p className={["mt-4 text-[10px] uppercase tracking-widest font-semibold",
        pass ? "text-white/80" : "text-pink-700"].join(" ")}>
        {pass ? "Certification quiz passed" : `Not yet — you need ${result.pass_pct}% to pass`}
      </p>
      <h2 className="font-display text-4xl mt-1">
        {result.correct}<span className={pass ? "text-white/70" : "text-pink-700"}>/{result.total}</span>
        <span className={["ml-4 text-2xl align-middle", pass ? "text-white/80" : "text-pink-700"].join(" ")}>
          {result.score_pct}%
        </span>
      </h2>
      <p className={["mt-3 text-sm md:text-base max-w-lg", pass ? "text-white/90" : "text-pink-900"].join(" ")}>
        {pass
          ? `${moduleName || "This module"} is now marked complete on your certificate journey. Nice work.`
          : cooldown > 0
          ? `You didn't hit ${result.pass_pct}% this time. You can retake in ${cooldownLabel} with a fresh, randomised set of questions.`
          : `You didn't hit ${result.pass_pct}% this time. Give it another shot — you'll see a fresh randomised set of questions.`}
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        {/* Review Answers is gated to passing users only — showing it on a
            fail would reveal every correct answer without demonstrating
            mastery. Failed users get Retake instead. */}
        {pass && (
          <button onClick={onReview} className={
            "text-xs font-semibold rounded-full px-5 py-2.5 bg-white/15 text-white hover:bg-white/25"
          } data-testid="quiz-review">
            {reviewOpen ? "Hide review" : "Review answers"}
          </button>
        )}
        {!pass && (
          <button onClick={onRetake}
            disabled={cooldown > 0}
            className="text-xs font-semibold bg-pink-800 text-white rounded-full px-5 py-2.5 hover:bg-pink-900 inline-flex items-center gap-1 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="quiz-retake">
            <RotateCcw className="w-3.5 h-3.5" />
            {cooldown > 0 ? `Wait ${cooldownLabel} before retaking` : "Retake quiz"}
          </button>
        )}
      </div>
    </div>
  );
}

function formatCooldown(seconds) {
  // Human-friendly duration: "23 hours 54 minutes", "12 minutes 8 seconds",
  // "45 seconds". Never shows raw seconds like "86362 seconds".
  if (!seconds || seconds <= 0) return "";
  const s = Math.max(0, Math.floor(seconds));
  const unit = (n, singular) => `${n} ${singular}${n === 1 ? "" : "s"}`;
  if (s < 60) return unit(s, "second");
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) {
    return rs ? `${unit(m, "minute")} ${unit(rs, "second")}` : unit(m, "minute");
  }
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm ? `${unit(h, "hour")} ${unit(rm, "minute")}` : unit(h, "hour");
}

function QuestionReviewCard({ q, index, review }) {
  const isTF = q.type === "TF";
  const letters = isTF ? ["True", "False"] : ["A", "B", "C", "D"].slice(0, q.options.length);
  return (
    <div className="surface p-5" data-testid={`quiz-review-q-${index}`}>
      <div className="flex items-start gap-3">
        <span className="chip shrink-0">Q{index + 1}</span>
        <p className="font-semibold leading-snug flex-1">{q.q}</p>
        {review?.correct ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                          : <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />}
      </div>
      <div className="mt-3 grid gap-1.5 text-sm">
        {q.options.map((opt, i) => {
          const letter = letters[i];
          const correctL = String(review?.correct_answer || "").toUpperCase();
          const chosenL = String(review?.your_answer || "").toUpperCase();
          const thisL = letter.toUpperCase();
          const isCorrect = thisL === correctL;
          const isWrong = thisL === chosenL && !isCorrect;
          return (
            <div key={i} className={[
              "rounded-lg px-3 py-2 flex items-center gap-3",
              isCorrect ? "bg-green-50 text-green-950 border border-green-200"
                        : isWrong ? "bg-red-50 text-red-950 border border-red-200"
                        : "bg-muted/40 text-muted-foreground",
            ].join(" ")}>
              <span className={[
                "w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold shrink-0",
                isCorrect ? "bg-green-600 text-white"
                          : isWrong ? "bg-red-500 text-white"
                          : "bg-muted-foreground/20",
              ].join(" ")}>{isTF ? letter[0] : letter}</span>
              <span className="flex-1">{opt}</span>
              {isCorrect && <span className="text-[10px] uppercase tracking-widest text-green-700 font-bold">Correct</span>}
              {isWrong && <span className="text-[10px] uppercase tracking-widest text-red-700 font-bold">Your pick</span>}
            </div>
          );
        })}
      </div>
      {review?.explanation && (
        <div className="mt-3 rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">Why: </span>{review.explanation}
        </div>
      )}
    </div>
  );
}

function IntroStat({ label, value }) {
  return (
    <div className="rounded-xl border border-shell-border bg-white px-4 py-3">
      <p className="font-display text-2xl">{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">{label}</p>
    </div>
  );
}
